import { Layers } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { useMetricsOverview } from '@/services/admin/metrics.service'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

/**
 * Lista global de módulos com taxa de conclusão. Diferente do
 * ModuleFunnelChart, esse mostra TODOS os módulos visíveis (não
 * filtrado por curso) — útil pra ver visão geral sem ter selecionado
 * curso no filtro.
 *
 * Dado vem do `useMetricsOverview.moduleCompletion`.
 */
export function ModuleCompletionList({ filters }: Props) {
  const { data, isLoading } = useMetricsOverview(filters.courseId, filters.companyIds)

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  const modules = data?.moduleCompletion ?? []

  return (
    <Card className="glass-card border-border/50 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Layers className="w-4 h-4 text-primary" />
          Conclusão por módulo (geral)
        </CardTitle>
        <CardDescription>Taxa de conclusão · {modules.length} módulos publicados</CardDescription>
      </CardHeader>
      <CardContent>
        {modules.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Sem módulos publicados no filtro.
          </p>
        ) : (
          <ul className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
            {modules.map((m) => (
              <li key={m.moduleId} className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-3 text-xs">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{m.moduleName}</p>
                    {m.courseName && (
                      <p className="text-[10px] text-muted-foreground truncate">{m.courseName}</p>
                    )}
                  </div>
                  <span className="text-muted-foreground tabular-nums whitespace-nowrap">
                    {m.completed}/{m.started}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={m.rate} className="h-2 flex-1" />
                  <span className="text-xs font-semibold tabular-nums w-10 text-right">
                    {m.rate}%
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
